/**
 * Filtros de UI sobre linhas mensais da sumarização (client-side).
 */

/**
 * Valor do filtro de base legal que seleciona as linhas SEM base legal.
 * Substitui o antigo filtro `compoeProjeto = 'nao'`: com `compoeProjeto` derivado da base
 * legal, "sem base legal" é exatamente o que aquele filtro queria dizer.
 * Espelha SEM_BASE_LEGAL_SENTINEL na api-relatorios.
 */
export const SEM_BASE_LEGAL = "sem";

function competenciaYm(competencia) {
  if (!competencia || competencia.length < 7) return "";
  return competencia.slice(0, 7);
}

/** Intervalo YYYY-MM das competências presentes nas linhas. */
export function competenciaRangeFromLinhas(linhas) {
  let min = "";
  let max = "";
  for (const linha of linhas || []) {
    const ym = competenciaYm(linha.competencia);
    if (!ym) continue;
    if (!min || ym < min) min = ym;
    if (!max || ym > max) max = ym;
  }
  return { min, max };
}

function parseNum(value) {
  if (value === "" || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {Array<object>} linhas
 * @param {object} filtros
 * @param {string} [filtros.competenciaInicio] YYYY-MM
 * @param {string} [filtros.competenciaFim] YYYY-MM
 * @param {string} [filtros.buscaConta] texto parcial
 * @param {string[]} [filtros.contasSelecionadas] códigos ECD selecionados
 * @param {string[]} [filtros.baseLegalSelecionadas] códigos de base legal selecionados (id ou SEM_BASE_LEGAL); vazio/ausente = todas
 * @param {number|string|null} [filtros.baseMin]
 * @param {number|string|null} [filtros.baseMax]
 * @param {number|string|null} [filtros.pisMin]
 * @param {number|string|null} [filtros.pisMax]
 * @param {number|string|null} [filtros.cofinsMin]
 * @param {number|string|null} [filtros.cofinsMax]
 */
export function filtrarLinhasSumarizacao(linhas, filtros = {}) {
  const lista = Array.isArray(linhas) ? linhas : [];
  const ini = String(filtros.competenciaInicio || "").trim();
  const fim = String(filtros.competenciaFim || "").trim();
  const busca = String(filtros.buscaConta || "")
    .trim()
    .toLowerCase();
  const contasSel = Array.isArray(filtros.contasSelecionadas)
    ? filtros.contasSelecionadas.map((c) => String(c).trim()).filter(Boolean)
    : null;
  const baseLegalSel = Array.isArray(filtros.baseLegalSelecionadas)
    ? filtros.baseLegalSelecionadas.map((c) => String(c).trim()).filter(Boolean)
    : null;
  const baseMin = parseNum(filtros.baseMin);
  const baseMax = parseNum(filtros.baseMax);
  const pisMin = parseNum(filtros.pisMin);
  const pisMax = parseNum(filtros.pisMax);
  const cofinsMin = parseNum(filtros.cofinsMin);
  const cofinsMax = parseNum(filtros.cofinsMax);

  return lista.filter((linha) => {
    const ym = competenciaYm(linha.competencia);
    if (ini && ym && ym < ini) return false;
    if (fim && ym && ym > fim) return false;

    if (busca) {
      const texto = `${linha.ecdContaContabil || ""} ${linha.contaContabil || ""}`.toLowerCase();
      if (!texto.includes(busca)) return false;
    }

    if (contasSel !== null && contasSel.length > 0) {
      const cod = String(linha.ecdContaContabil || "").trim();
      if (!contasSel.includes(cod)) return false;
    }

    if (baseLegalSel !== null && baseLegalSel.length > 0) {
      const codLinha = linha.baseLegalId == null ? SEM_BASE_LEGAL : String(linha.baseLegalId);
      if (!baseLegalSel.includes(codLinha)) return false;
    }

    const base = Number(linha.base) || 0;
    const pis = Number(linha.pis) || 0;
    const cofins = Number(linha.cofins) || 0;
    if (baseMin != null && base < baseMin) return false;
    if (baseMax != null && base > baseMax) return false;
    if (pisMin != null && pis < pisMin) return false;
    if (pisMax != null && pis > pisMax) return false;
    if (cofinsMin != null && cofins < cofinsMin) return false;
    if (cofinsMax != null && cofins > cofinsMax) return false;

    return true;
  });
}
